import React, { useState } from 'react';
import { Box, Typography, IconButton, List, ListItem, ListItemAvatar, Avatar, ListItemText, ListItemSecondaryAction, Chip, Menu, MenuItem } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import FavoriteIcon from '@mui/icons-material/Favorite';
import PhoneMissedIcon from '@mui/icons-material/PhoneMissed';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

const ActivityView = ({ searchQuery = '' }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [filter, setFilter] = useState('all');


    const dummyNotifications = [
        {
            id: 1,
            type: 'mention',
            user: 'Sarah Miller',
            content: 'mentioned you in general',
            time: '2m ago',
            unread: true,
            icon: <AlternateEmailIcon sx={{ fontSize: 14, color: '#fff' }} />,
            iconBg: '#4b53bc'
        },
        {
            id: 2,
            type: 'reaction',
            user: 'Alex Johnson',
            content: 'reacted to your message: "Thanks!"',
            time: '15m ago',
            unread: false,
            icon: <FavoriteIcon sx={{ fontSize: 14, color: '#fff' }} />,
            iconBg: '#f33a6a'
        },
        {
            id: 3,
            type: 'call',
            user: 'Tech Lead',
            content: 'Missed call from Tech Lead',
            time: '1h ago',
            unread: true,
            icon: <PhoneMissedIcon sx={{ fontSize: 14, color: '#fff' }} />,
            iconBg: '#f44336'
        },
        {
            id: 4,
            type: 'reply',
            user: 'DevOps Team',
            content: 'replied to your thread in engineering',
            time: '3h ago',
            unread: false,
            icon: <ChatBubbleOutlineIcon sx={{ fontSize: 14, color: '#fff' }} />,
            iconBg: '#616161'
        },
        {
            id: 5,
            type: 'mention',
            user: 'Sarah Miller',
            content: 'mentioned you in design',
            time: 'Yesterday',
            unread: false,
            icon: <AlternateEmailIcon sx={{ fontSize: 14, color: '#fff' }} />,
            iconBg: '#4b53bc'
        }
    ];

    const filteredNotifications = dummyNotifications.filter(n => {
        if (filter === 'mentions') return n.type === 'mention';
        if (filter === 'unread') return n.unread;
        return true;
    });

    const handleFilterClick = (event) => setAnchorEl(event.currentTarget);
    const handleFilterClose = (type) => {
        if (type) setFilter(type);
        setAnchorEl(null);
    };

    return (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#fff' }}>
            {/* Header */}
            <Box sx={{
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                borderBottom: '1px solid #e1e1e1'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontSize: '18px', fontWeight: 600 }}>
                        Activity
                    </Typography>
                    {filter !== 'all' && (
                        <Chip
                            label={filter}
                            size="small"
                            onDelete={() => setFilter('all')}
                            sx={{ height: 20, fontSize: '10px', textTransform: 'capitalize' }}
                        />
                    )}
                </Box>
                <Box>
                    <IconButton size="small" onClick={handleFilterClick}>
                        <FilterListIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small">
                        <SettingsIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>

            {/* Filter Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => handleFilterClose()}
            >
                <MenuItem onClick={() => handleFilterClose('all')}>All Activity</MenuItem>
                <MenuItem onClick={() => handleFilterClose('mentions')}>Mentions</MenuItem>
                <MenuItem onClick={() => handleFilterClose('unread')}>Unread</MenuItem>
            </Menu>

            {/* Content Area */}
            <Box sx={{ flex: 1, overflowY: 'auto' }}>
                <List sx={{ p: 0 }}>
                    {filteredNotifications.length > 0 ? (
                        filteredNotifications.map((notif) => (
                            <ListItem
                                key={notif.id}
                                button
                                sx={{
                                    px: 2,
                                    py: 1.5,
                                    borderBottom: '1px solid #f0f0f0',
                                    bgcolor: notif.unread ? 'rgba(75, 83, 188, 0.04)' : 'transparent',
                                    '&:hover': { bgcolor: '#f9f9f9' },
                                    position: 'relative'
                                }}
                            >
                                <ListItemAvatar sx={{ minWidth: 56, position: 'relative' }}>
                                    <Avatar sx={{ width: 40, height: 40, bgcolor: '#f3f2f1', color: '#616161', fontSize: 14 }}>
                                        {notif.user.charAt(0)}
                                    </Avatar>
                                    <Box sx={{
                                        position: 'absolute',
                                        bottom: 0,
                                        right: 12,
                                        width: 18,
                                        height: 18,
                                        bgcolor: notif.iconBg,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: '2px solid #fff'
                                    }}>
                                        {notif.icon}
                                    </Box>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={
                                        <Typography sx={{ fontWeight: notif.unread ? 700 : 500, fontSize: '14px', color: '#242424' }}>
                                            {notif.user}
                                        </Typography>
                                    }
                                    secondary={
                                        <Typography variant="body2" sx={{ fontSize: '13px', color: notif.unread ? '#242424' : '#616161', fontWeight: notif.unread ? 600 : 400 }} noWrap>
                                            {notif.content}
                                        </Typography>
                                    }
                                />
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                                    <Typography variant="caption" color="textSecondary">
                                        {notif.time}
                                    </Typography>
                                    {notif.unread && (
                                        <Box sx={{ width: 8, height: 8, bgcolor: '#4b53bc', borderRadius: '50%' }} />
                                    )}
                                </Box>
                            </ListItem>
                        ))
                    ) : (
                        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, textAlign: 'center' }}>
                            <Typography color="textSecondary">No notifications found.</Typography>
                        </Box>
                    )}
                </List>
            </Box>
        </Box>
    );
};

export default ActivityView;
